import React, { Component } from 'react';
import { HashRouter, Redirect, Route, Switch, BrowserRouter } from 'react-router-dom';
import './scss/style.scss';
import './scss/mobile.css';
import IdleTimer from 'react-idle-timer'
import { ClearLocalStorage } from "src/helpers/ClearLocalStorage";
import toast, { Toaster } from "react-hot-toast";
import { init, logEvent } from '@amplitude/analytics-browser';
import { MobileProvider } from './context/MobileContext';
import { shouldShowMobileView } from './utils/deviceDetection';
import { handleRedirectPromise } from './views/MSIntegration/SSOLogin';
import { requestNavigatorSignOut } from './helpers/NavigatorSessionBridge';
import { sanitizeTelemetryUrl } from './helpers/SanitizeTelemetryUrl';

//initialize LogRocket
import LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import ForgotPassword from './views/pages/forgot_password/ForgotPassword';

init("d16992c930ee5a387092050fcb6987d5");

try {
  LogRocket.init('4pkmqo/ebh', {
    dom: {
      textSanitizer: true,
      inputSanitizer: true,
    },
    network: {
      requestSanitizer: request => {
        // Authentication and healthcare request bodies never belong in
        // session-replay telemetry. Keep only method/URL metadata.
        request.body = null;
        return request;
      },
      responseSanitizer: response => {
        if (response.headers['x-secret']) {
          // removes all response data
          return null;
        }
        // scrubs response body
        response.body = null;
        return response;
      },
    },
    browser: {
      urlSanitizer: sanitizeTelemetryUrl,
    },
  });

} catch (error) {
  toast.error("LogRocket Error: " + error.message);
  // Additional error handling, if necessary
}


// after calling LogRocket.init()
setupLogRocketReact(LogRocket);


const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
)

// timeout for tab/browser close function
window.onbeforeunload = () => {
  window.localStorage.unloadTime = JSON.stringify(new Date());
};

window.onload = async () => {
  let loadTime = new Date();
  let unloadTime = new Date(JSON.parse(window.localStorage.unloadTime));
  let refreshTime = loadTime.getTime() - unloadTime.getTime();

  if (refreshTime > 1000 * 60 * 15)     //   5 minutes timeout
  {
    await requestNavigatorSignOut();
    ClearLocalStorage();
  }
};

// Containers
const TheLayout = React.lazy(() => import('./containers/TheLayout'));
const MobileLayout = React.lazy(() => import('./containers/mobile/MobileLayout'));


// Pages
const Login = React.lazy(() => import('./views/pages/login/Login'));

// Check for OAuth redirect callback on page load (before React Router initializes)
// This is critical for mobile devices where hash fragments might be lost
let hasOAuthCallback = false;
if (typeof window !== 'undefined' && !localStorage.getItem('token')) {
  hasOAuthCallback = window.location.hash && (
    window.location.hash.includes('code=') || 
    window.location.hash.includes('error=') ||
    window.location.hash.includes('state=')
  );
}

class App extends Component {

  state = {
    token: localStorage.getItem('token'),
    isHandlingRedirect: hasOAuthCallback,
  }

  async componentDidMount() {
    // Handle Microsoft SSO redirect callback BEFORE checking token
    // This ensures hash fragments are processed before React Router redirects
    const hasHashFragment = window.location.hash && (
      window.location.hash.includes('code=') || 
      window.location.hash.includes('error=') ||
      window.location.hash.includes('state=')
    );

    if (hasHashFragment && !localStorage.getItem('token')) {
      this.setState({ isHandlingRedirect: true });
      try {
        console.log("Detected OAuth callback, handling redirect promise...");
        const response = await handleRedirectPromise();
        if (response) {
          console.log("OAuth redirect handled successfully");
          // The redirect will be handled by handleRedirectPromise -> processSSOLogin
          // which will set the token and reload, so we don't need to update state here
          return;
        }
      } catch (err) {
        console.error("Error handling redirect promise:", err);
        // Clear hash fragment on error to prevent infinite redirect loops
        if (window.location.hash) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      } finally {
        this.setState({ isHandlingRedirect: false });
      }
    }

    this.setState({
      token: localStorage.getItem('token'),
    });

    // Also handle redirect promise if no hash fragment but no token (fallback)
    if (!hasHashFragment && !localStorage.getItem('token')) {
      try {
        await handleRedirectPromise();
      } catch (err) {
        console.log("Error handling redirect promise (fallback):", err);
      }
    }
  }

  constructor(props) {
    super(props)
    this.idleTimer = null
    this.handleOnIdle = this.handleOnIdle.bind(this)
  }

  render() {
    const isMobileView = shouldShowMobileView();

    // Show loading state while handling OAuth redirect to prevent React Router from interfering
    if (this.state.isHandlingRedirect) {
      return (
        <div className="pt-3 text-center">
          <div className="sk-spinner sk-spinner-pulse" />
          <p>Completing login...</p>
        </div>
      );
    }

    return (
      <MobileProvider>
        <BrowserRouter>
          {/*    timeout function for idle screen   */}
          {this.state.token &&
            <IdleTimer
              ref={ref => {
                this.idleTimer = ref
              }}
              timeout={1000 * 60 * 15} //   15 minutes timeout
              crossTab={{
                emitOnAllTabs: true
              }}
              onIdle={this.handleOnIdle}
              debounce={250}
            />
          }
          <React.Suspense fallback={loading}>
            <Toaster />
            {!this.state.token ? (
              <Switch onPress={() => logEvent('user-login')}>
                <Route exact path="/login" name="Login Page" render={props => <Login {...props} />} />
                <Route exact path="/forgot-password" name="Forgot Password" render={props => <ForgotPassword {...props} />} />
                <Redirect from="/" to="/login" />
              </Switch>
            ) : (
              <Switch>
                {/* Mobile routes */}
                <Route path="/mobile" name="Mobile" render={props => <MobileLayout {...props} />} />
                {/* Auto-redirect to mobile on mobile devices when accessing /referrals */}
                {isMobileView && (
                  <Route exact path="/referrals" render={() => <Redirect to="/mobile/referral" />} />
                )}
                {/* Default desktop routes */}
                <Route path="/" name="Home" render={props => <TheLayout {...props} />} />
              </Switch>
            )}
          </React.Suspense>
        </BrowserRouter>
      </MobileProvider>
    );
  }

  async handleOnIdle() {
    await requestNavigatorSignOut();
    ClearLocalStorage();
  }

}

export default App;
